/**
 * autoScroll.js - while a Dashboard widget is dragged or resized, a pointer
 * near the top or bottom edge of the scrolling area scrolls it, so a widget
 * can travel the whole page without a mouse wheel (10/12/26, Sharif: "I
 * can't drag and go up unless I have a mouse and scroll up").
 *
 * The app's pages scroll inside a container (App.jsx .app-content-padding),
 * not the window: scrollParentOf finds whichever it is.
 */

/** Pixels from an edge where scrolling starts, and the fastest step (px per frame). */
export const EDGE_PX = 72;
export const MAX_STEP = 22;

/**
 * Scroll step (px per frame) for a pointer at `y` in a scrolling area that
 * spans `top`..`bottom` on screen: negative near the top, positive near the
 * bottom, 0 in between. It grows with how deep into the edge band the
 * pointer is (eased, so the first pixels creep and the edge itself is fast);
 * past the edge (over the app header, below the window) it is the fastest.
 */
export function scrollStep(y, top, bottom, edge = EDGE_PX, max = MAX_STEP) {
  if (!Number.isFinite(y) || !(bottom > top)) return 0;
  const band = Math.min(edge, (bottom - top) / 3);
  if (band <= 0) return 0;
  const depth = (into) => {
    const t = Math.max(0, Math.min(1, into / band));
    return Math.max(1, Math.round(max * t * t));
  };
  if (y < top + band) return -depth(top + band - y);
  if (y > bottom - band) return depth(y - (bottom - band));
  return 0;
}

const SCROLLS = /(auto|scroll|overlay)/;

/**
 * The element that scrolls `el` vertically: the closest ancestor whose
 * overflow-y scrolls and whose content is taller than it, else the page.
 */
export function scrollParentOf(el) {
  const doc = (el && el.ownerDocument) || document;
  for (let p = el && el.parentElement; p && p !== doc.body && p !== doc.documentElement; p = p.parentElement) {
    let oy = '';
    try {
      oy = window.getComputedStyle(p).overflowY;
    } catch {
      oy = '';
    }
    if (SCROLLS.test(oy) && p.scrollHeight > p.clientHeight) return p;
  }
  return doc.scrollingElement || doc.documentElement;
}

/** The on-screen top and bottom of a scroller (the page's is the window). */
export function viewportOf(scroller) {
  const doc = scroller.ownerDocument || document;
  if (scroller === doc.scrollingElement || scroller === doc.documentElement || scroller === doc.body) {
    return { top: 0, bottom: window.innerHeight || doc.documentElement.clientHeight || 0 };
  }
  const r = scroller.getBoundingClientRect();
  // never past the window's own edges (a scroller taller than the window)
  return { top: Math.max(0, r.top), bottom: Math.min(window.innerHeight || r.bottom, r.bottom) };
}
